import React, { useState } from "react";
import { ArrowLeft, ImageIcon, CircleAlert } from "lucide-react";
import PermitHeaderMeta from "./PermitHeaderMeta";
import PTWSignOffSection from "./PTWSignOffSection";
import PermitQuestionDiscussion from "./PermitQuestionDiscussion";
import PermitDescriptionBox from "./PermitDescriptionBox";
import { resolveContractorName, isContractorInternal } from "./PermitSignatureCard";
import { getPermitInternalTeamName } from "./PermitListMeta";
import { showToast } from "../../../../utils/toast";
import TbtAttendanceModal from "../PermitDashboard/TbtAttendanceModal";
import {
  downloadPermitReport,
  downloadPermitTBTAttendanceReport,
} from "../../../../api";
import { resolveMediaUrl } from "../../../../lib/utils";
import {
  canShowPermitReportDownload,
  formatStatusLabel,
  getStatusBadgeClass,
  isProvidedPpeField,
  getPermitRejectionBadges,
  extractAnswerString,
  extractOptionValue,
  extractOptionString,
  getAnswerBadgeClasses,
  isSpecialCellDisabled,
  isTemperaturePermissibleLimitCell,
  shouldShowSpecialField,
  evaluateSectionShowWhen,
} from "./permitHelpers";
import { buildChecklistSections } from "./permitChecklistSections";
import PermitChecklistSectionTabs from "./PermitChecklistSectionTabs";
import ProvidedPpeBadges from "./ProvidedPpeBadges";
import PermitChecklistPhotosList from "./PermitChecklistPhotosList";

const renderFieldValue = (val) => {
  if (val === null || val === undefined || val === "") return "N/A";
  if (Array.isArray(val)) {
    return val
      .map((item) => (typeof item === "object" ? renderFieldValue(item) : String(item)))
      .join(", ");
  }
  if (typeof val === "object") {
    const parts = Object.entries(val)
      .filter(([_, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => (typeof v === "object" ? `${k}: ${JSON.stringify(v)}` : String(v)));
    return parts.length > 0 ? parts.join(" - ") : "N/A";
  }
  return String(val);
};

const getQuestionImages = (permit, questionId) => {
  return (permit?.checklist_images || []).filter(
    (img) => String(img.question_id) === String(questionId),
  );
};

const getImageUrl = (img) => resolveMediaUrl(img?.file_url || img?.file || "");

const getSignatureUrl = (sig) =>
  resolveMediaUrl(sig?.signature_url || sig?.signature || "");

const getAllActionSignatures = (permit) => {
  const logs = permit?.workflow_logs || [];

  return logs.flatMap((log) =>
    (log.signatures || []).map((sig) => ({
      ...sig,
      log_action: log.action,
      log_status_from: log.status_from,
      log_status_to: log.status_to,
      log_performed_at: log.performed_at || log.created_at,
      log_actor_name: log.performed_by_name,
      log_actor_group_name: log.actor_group_name,
    })),
  );
};

const formatValue = (val) => {
  if (Array.isArray(val)) {
    return val.join(", ");
  }
  if (typeof val === "object" && val !== null) {
    return Object.values(val).filter(Boolean).join(" - ");
  }
  return val;
};

const findSignatureForBox = (permit, box) => {
  const signatures = getAllActionSignatures(permit);

  return signatures.find((sig) => {
    if (box.key && sig.signature_box_key === box.key) return true;

    return (
      sig.action === box.action &&
      String(sig.actor_group_id || "") === String(box.signing_group_id || "") &&
      String(sig.log_status_from || "") === String(box.required_status || "")
    );
  });
};

export default function PermitReadonlyView({
  permit,
  onBack,
  titlePrefix = "View Permit",
  onCancelPermit,
}) {
  const [tbtViewModalOpen, setTbtViewModalOpen] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloadingTbtReport, setDownloadingTbtReport] = useState(false);

  if (!permit) return null;

  const openTbtViewModal = () => {
    if (!permit?.id) {
      showToast("Permit not found", "error");
      return;
    }
    setTbtViewModalOpen(true);
  };

  const handleDownloadReport = async () => {
    if (!permit?.id) return;
    setDownloadingReport(true);
    try {
      await downloadPermitReport(permit.id);
      showToast("Report downloaded successfully", "success");
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to download report";
      showToast(message, "error");
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleDownloadTbtReport = async () => {
    if (!permit?.id) return;
    setDownloadingTbtReport(true);
    try {
      await downloadPermitTBTAttendanceReport(permit.id);
      showToast("TBT Attendance Report downloaded successfully", "success");
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to download TBT attendance report";
      showToast(message, "error");
    } finally {
      setDownloadingTbtReport(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl 2xl:max-w-7xl">
      <button
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Dashboard
      </button>

      <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-bold text-foreground sm:text-xl">
            {titlePrefix} -{" "}
            {permit.template_snapshot?.template_name ||
              permit.template_name ||
              `Permit #${permit.id}`}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span className="flex items-center gap-1.5">
              <span>Current Status:</span>
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusBadgeClass(permit.workflow_summary?.current_status || permit.current_status)}`}>
                {formatStatusLabel(permit.workflow_summary?.current_status || permit.current_status)}
              </span>
            </span>

            {getPermitRejectionBadges(permit).map((badge, idx) => (
              <span
                key={`${badge.type}-${idx}`}
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${badge.className}`}
              >
                {badge.label}
                {badge.isLimitReached ? " · Limit Reached" : ""}
              </span>
            ))}
          </div>
        </div>

        {canShowPermitReportDownload(permit) && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownloadReport}
              disabled={downloadingReport}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-green-700 disabled:opacity-50"
            >
              {downloadingReport ? "Downloading..." : "Download Permit Report"}
            </button>

            <button
              type="button"
              onClick={handleDownloadTbtReport}
              disabled={downloadingTbtReport}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 disabled:opacity-50"
            >
              {downloadingTbtReport ? "Downloading..." : "Download TBT Attendance"}
            </button>
          </div>
        )}
      </div>

      {/* Cancellation Details Banner */}
      {(permit.workflow_summary?.current_status === "cancelled" || permit.current_status === "cancelled") && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3 text-red-700">
            <div className="flex items-center gap-2 font-bold text-base">
              <CircleAlert className="h-5 w-5 shrink-0" />
              <span>Permit Cancelled</span>
            </div>
            {permit.latest_cancelled_at_display && (
              <span className="text-xs font-semibold text-red-600">
                {permit.latest_cancelled_at_display}
              </span>
            )}
          </div>

          <div className="mt-2 text-xs text-red-800">
            {permit.latest_cancelled_by_name && (
              <p>
                <span className="font-semibold text-red-900">Cancelled by:</span>{" "}
                {permit.latest_cancelled_by_name}
              </p>
            )}
            {permit.cancel_count > 0 && (
              <p className="mt-0.5 text-red-700">
                <span className="font-semibold">Cancellation Cycle:</span> {permit.cancel_count} of 3
                {permit.is_final_cancelled ? " (Cancellation Limit Reached)" : ""}
              </p>
            )}
          </div>

          {permit.latest_cancel_reason ? (
            <div className="mt-3 rounded-lg border border-red-200 bg-white p-3.5 shadow-2xs">
              <p className="text-[11px] font-bold uppercase tracking-wider text-red-600 mb-1">
                Cancellation Reason / Remarks
              </p>
              <p className="whitespace-pre-wrap text-sm font-medium text-slate-800 leading-relaxed">
                {permit.latest_cancel_reason}
              </p>
            </div>
          ) : null}
        </div>
      )}

      <PermitHeaderMeta
        headerConfig={permit.template_snapshot?.header_config}
        formatNo={permit.template_snapshot?.format_no}
        refNo={permit.template_snapshot?.ref_no}
        issuedDateText={permit.template_snapshot?.issued_date_text}
        revisionNo={permit.template_snapshot?.revision_no}
        projectId={permit.project_id}
        permitId={permit.id}
        ptwNo={permit.ptw_no}
        permit={permit}
      />

      <div className="space-y-6">
        {/* SECTION 1: Permit Details */}
        {permit?.template_snapshot?.field_definitions?.length > 0 && (
          <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
            <h3 className="mb-4 text-md font-semibold text-slate-800">
              Permit Details
            </h3>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {permit.template_snapshot.field_definitions.map((field) => {
                let value = permit.form_data?.[field.key];
                if (field.key === "contractor_name") {
                  const resolved =
                    resolveContractorName(permit) ||
                    permit.contractor_name ||
                    permit.form_data?.contractor_name ||
                    value;
                  const internalDisplay = getPermitInternalTeamName(permit);
                  value = isContractorInternal(resolved, permit)
                    ? internalDisplay
                    : resolved || internalDisplay;
                }
                const isPpe = isProvidedPpeField(field);

                return (
                  <div
                    key={field.key}
                    className={`rounded-lg border bg-slate-50 p-3 ${
                      isPpe ? "sm:col-span-2" : ""
                    }`}
                  >
                    <p className="text-xs font-semibold text-slate-500">
                      {field.key === "contractor_name"
                        ? "Organization / Contractor"
                        : field.label}
                    </p>
                    {isPpe ? (
                      <ProvidedPpeBadges value={value} />
                    ) : (
                      <p className="mt-1 text-sm font-medium text-slate-900">
                        {renderFieldValue(value)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* SECTION 2: Permit Description */}
        <PermitDescriptionBox
          description={
            permit?.template_snapshot?.description ||
            permit?.template?.description
          }
          title="Permit Description"
          titleClassName="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500"
        />

        {/* SECTION 3: Checklist Answers */}
        {permit?.template_snapshot?.checklist_questions?.length > 0 && (() => {
          const readonlySectionGroups = buildChecklistSections({
            sections: permit.template_snapshot.checklist_sections || [],
            questions: permit.template_snapshot.checklist_questions || [],
          });
          const firstQId = permit.template_snapshot.checklist_questions?.[0]?.id;

          return (
            <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
              <h3 className="mb-4 text-md font-semibold text-slate-800">
                Checklist Answers
              </h3>

              {(() => {
                const getReadonlyBadgeInfo = (group) => {
                  const commentedCount = group.questions.filter((q) => {
                    const resp = permit?.checklist_response?.find(
                      (r) => String(r.question_id ?? r.id) === String(q.id)
                    );
                    if (resp?.remarks && String(resp.remarks).trim()) return true;
                    const qLogs = permit?.question_logs?.[String(q.id)];
                    const comments = Array.isArray(qLogs?.comments) ? qLogs.comments : [];
                    return comments.some((c) => {
                      const src = String(c.source || "").toLowerCase();
                      const isMaker = src.includes("maker") || src === "resubmit";
                      const isCreator = permit?.created_by_id && c.user_id && Number(c.user_id) === Number(permit.created_by_id);
                      return (isMaker || isCreator) && Boolean(String(c.comment || "").trim());
                    });
                  }).length;

                  if (commentedCount > 0) {
                    return {
                      label: `${commentedCount} commented`,
                      colorCls: "bg-amber-500 text-white font-bold",
                    };
                  }
                  return null;
                };

                return (
                  <PermitChecklistSectionTabs
                    sectionGroups={readonlySectionGroups}
                    getBadgeInfo={getReadonlyBadgeInfo}
                  >
                    {(currentGroup) => (
                      <div className="space-y-4">
                        {currentGroup.questions.map((q, index) => {
                          const response = permit.checklist_response?.find(
                            (r) => String(r.question_id ?? r.id) === String(q.id),
                          );
                          const images = getQuestionImages(permit, q.id);
                          const answer = extractAnswerString(response?.answer) || "No answer";
                          const isFirstQuestionOverall = q.id === firstQId;
                          const qNumber = index + 1;

                          const makerComment =
                            (response?.remarks && String(response.remarks).trim()) ||
                            (() => {
                              const qLogs = permit?.question_logs?.[String(q.id)];
                              const comments = Array.isArray(qLogs?.comments) ? qLogs.comments : [];
                              const found = [...comments].reverse().find((c) => {
                                const src = String(c.source || "").toLowerCase();
                                const isMaker = src.includes("maker") || src === "resubmit";
                                const isCreator = permit?.created_by_id && c.user_id && Number(c.user_id) === Number(permit.created_by_id);
                                return (isMaker || isCreator) && Boolean(String(c.comment || "").trim());
                              });
                              return found?.comment ? String(found.comment).trim() : "";
                            })();

                          const hasMakerComment = Boolean(makerComment);

                          return (
                            <div
                              key={q.id}
                              className={`rounded-xl border p-5 shadow-sm transition-all ${
                                hasMakerComment
                                  ? "border-amber-300 bg-amber-50/70 ring-1 ring-amber-200"
                                  : "border-slate-200 bg-card"
                              }`}
                            >
                              <div className="mb-3 flex items-start gap-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white">
                                  {qNumber}
                                </span>

                                <div className="flex-1">
                                  <div className="flex items-start justify-between gap-3">
                                    <p className="text-sm font-semibold text-slate-800">
                                      {q.question}
                                    </p>
                                    <div className="flex shrink-0 items-center gap-2">
                                      {isFirstQuestionOverall && (
                                        <button
                                          type="button"
                                          onClick={openTbtViewModal}
                                          className="shrink-0 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                                        >
                                          TBT Attendance
                                        </button>
                                      )}

                                      {hasMakerComment && (
                                        <span className="rounded-full bg-amber-100 border border-amber-300 px-2.5 py-1 text-xs font-semibold text-amber-800 flex items-center gap-1 shadow-2xs">
                                          <span>💬</span> Maker Commented
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <p className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                                    Answer:{" "}
                                    <span
                                      className={`inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-bold uppercase ${getAnswerBadgeClasses(
                                        answer
                                      )}`}
                                    >
                                      {answer}
                                    </span>
                                  </p>

                                  {hasMakerComment && (
                                    <div className="mt-3 rounded-lg border border-amber-300 bg-amber-100/70 p-3 text-xs shadow-2xs">
                                      <p className="font-bold text-amber-900 flex items-center gap-1.5">
                                        <span>💬 Maker Comment:</span>
                                      </p>
                                      <p className="mt-1 text-amber-950 font-medium whitespace-pre-wrap">{makerComment}</p>
                                    </div>
                                  )}

                                  {images.length > 0 && (
                                    <PermitChecklistPhotosList images={images} />
                                  )}

                                  <PermitQuestionDiscussion
                                    logs={permit.question_logs?.[String(q.id)]}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </PermitChecklistSectionTabs>
                );
              })()}
            </section>
          );
        })()}

        {/* SECTION 4: Special Sections */}
        {permit?.template_snapshot?.special_sections?.length > 0 && (
          <section className="space-y-6">
            {permit.template_snapshot.special_sections.map((section) => (
              <div key={section.key} className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                <h3 className="mb-4 text-md font-semibold text-slate-800">
                  {section.title}
                </h3>

                {section.type === "row_table" ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-slate-300 text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="w-12 border border-slate-300 p-2 text-left">
                            S.No
                          </th>
                          {section.columns?.map((col) => (
                            <th
                              key={col.key}
                              className="border border-slate-300 p-2 text-left"
                            >
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.rows?.map((row, idx) => (
                          <tr key={row.key}>
                            <td className="border border-slate-300 p-2">
                              {idx + 1}
                            </td>
                            {section.columns?.map((col) => {
                              if (
                                isSpecialCellDisabled(
                                  row,
                                  col,
                                  section,
                                  permit?.form_data
                                )
                              ) {
                                return (
                                  <td
                                    key={col.key}
                                    className="border border-slate-300 p-2 text-slate-800"
                                  >
                                    {""}
                                  </td>
                                );
                              }

                              const val =
                                permit.form_data?.[section.key]?.rows?.[
                                row.key
                                ]?.[col.key] ??
                                row[col.key] ??
                                "";

                              return (
                                <td
                                  key={col.key}
                                  className="border border-slate-300 p-2 text-slate-800"
                                >
                                  {formatValue(val) || "-"}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : section.type === "table" ? (
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    {section.fields?.map((f) => {
                      const val =
                        permit.form_data?.[section.key]?.[f.key] ?? "";
                      return (
                        <div
                          key={f.key}
                          className="rounded-lg border bg-slate-50 p-3"
                        >
                          <p className="text-xs font-medium text-slate-500">
                            {f.label}
                          </p>
                          <p className="mt-1 text-sm font-medium text-slate-900">
                            {formatValue(val) || "N/A"}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : section.type === "conditional_group" ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    {section.condition_field && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-slate-500">
                          {section.condition_field.label}
                        </p>
                        <p className="mt-1 text-sm font-medium text-slate-900">
                          {formatValue(
                            permit.form_data?.[section.key]?.[
                              section.condition_field.key
                            ]
                          ) || "N/A"}
                        </p>
                      </div>
                    )}

                    {evaluateSectionShowWhen(
                      section.show_when,
                      permit.form_data?.[section.key]
                    ) && (
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {section.fields?.map((f) => {
                          if (
                            !shouldShowSpecialField(
                              f,
                              permit.form_data?.[section.key]
                            )
                          ) {
                            return null;
                          }

                          const val =
                            permit.form_data?.[section.key]?.[f.key] ?? "";

                          let displayVal = formatValue(val);
                          if (
                            f.field_type === "select" &&
                            Array.isArray(f.options)
                          ) {
                            const match = f.options.find(
                              (opt) =>
                                String(extractOptionValue(opt)).trim().toLowerCase() ===
                                String(val ?? "").trim().toLowerCase()
                            );
                            if (match) {
                              displayVal = extractOptionString(match);
                            }
                          }

                          return (
                            <div
                              key={f.key}
                              className="rounded-lg border bg-white p-3"
                            >
                              <p className="text-xs font-medium text-slate-500">
                                {f.label}
                              </p>
                              <p className="mt-1 text-sm font-medium text-slate-900">
                                {displayVal || "N/A"}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            ))}
          </section>
        )}

        {/* SECTION 5: Signatures & Actions */}
        <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
          <PTWSignOffSection permit={permit} />

          {permit?.workflow_permissions?.can_cancel && onCancelPermit && (
            <div className="mt-6 flex justify-end border-t border-border/60 pt-4">
              <button
                type="button"
                onClick={() => onCancelPermit(permit)}
                className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-5 py-2.5 text-sm font-bold text-red-700 transition-all hover:bg-red-100 shadow-sm cursor-pointer"
              >
                Cancel Permit
              </button>
            </div>
          )}
        </section>
      </div>

      <TbtAttendanceModal
        isOpen={tbtViewModalOpen}
        onClose={() => setTbtViewModalOpen(false)}
        permit={permit}
        projectId={permit?.project_id}
        mode="api"
        readonly={true}
      />
    </div>
  );
}